import React, { useRef, useState } from 'react';
import axios from 'axios';
import Layout from '../../common/layout/Layout';
import { LoadingAnimation } from '../../common/components/Utilities';
import { NavLink, RenderNavigation, UserNavigation } from '../../common/layout/Sidebar';
import BaseLayout from '../../common/layout/BaseLayout';

function PeopleLayout(props) {
  return (
    <Layout
      {...props}
      navItems={<PeopleNavItems {...props} />}
      userNavItems={<PeopleUserNavItems {...props} />}
      search={<PeopleSearch />}
    />
  );
}

export function BasicPeopleLayout(props) {
  return (
    <BaseLayout
      {...props}
      navItems={<PeopleNavItems {...props} />}
      userNavItems={<PeopleUserNavItems {...props} />}
      search={<PeopleSearch />}
    />
  );
}

export const PeopleNavItems = ({ activeUser, groups }) => {
  const nav = [
    {
      url: '/', icon: 'house', label: 'Home', match: /^\/$/,
    },
    {
      icon: 'plane-departure',
      label: 'Leave',
      items: [
        { url: `/users/${activeUser.id}/leave`, label: 'My Leave' },
        { url: '/leave/create', label: 'Request Leave' },
        { url: '/approvals', label: 'Leave Approvals', groups: [groups.TEAM_LEADS, groups.OPERATIONS] },
        { url: '/calendar', label: 'Calendar' },
      ],
    },
    {
      url: `/users/${activeUser.id}/objectives`,
      match: /(^\/users\/[0-9]+\/objectives|^\/objectives)/,
      icon: 'tasks',
      label: 'Objectives',
    },
    {
      icon: 'users',
      label: 'People',
      items: [
        { url: '/squads', label: 'Squads' },
        { url: '/users', match: /^\/users$/, label: 'All People' },
        { url: '/recruitment', label: 'Recruitment', groups: [groups.RECRUITMENT] },
      ],
    },
    {
      icon: 'users-cog',
      label: 'Admin',
      groups: [groups.OPERATIONS],
      items: [
        { url: '/approvals?all=1', match: /^\/approvals\?all=1$/, label: 'Pending Leave Approvals' },
        { url: '/admin/jobs', label: 'Manage Roles' },
      ],
    },
    {
      icon: 'chart-line',
      label: 'Reports',
      groups: [groups.OPERATIONS],
      url: '/reports',
    },
  ];

  return nav.map((item) => RenderNavigation(activeUser, item));
};

export function PeopleUserNavItems(props) {
  const { activeUser } = props;

  return (
    <UserNavigation {...props}>
      <NavLink url={`/users/${activeUser.id}`} match={/^\/users\/[0-9]+$/} icon="user" size="sm" label="My Profile" />
      <NavLink url={`/users/${activeUser.id}/sickness`} icon="clinic-medical" size="sm" label="My Sickness" />
      <NavLink url="/profile" icon="key" size="sm" label="Update Profile" />
    </UserNavigation>
  );
}

export function PeopleSearch() {
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState([]);
  const [candidateData, setCandidateData] = useState([]);
  const searchRef = useRef();

  const debounce = (func, wait) => {
    let timeout;

    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };

      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const doSearch = debounce(() => {
    setLoading(true);
    axios.get(`/users/autocomplete?q=${searchRef.current.value}`)
      .then(({ data }) => {
        setUserData(data);
        setLoading(false);
      });

    axios.get(`/candidates/autocomplete?q=${searchRef.current.value}`)
      .then(({ data }) => {
        setCandidateData(data);
        setLoading(false);
      });
  }, 200);

  const results = [];

  if (userData.length) {
    const users = (
      <div key="users">
        {userData.map((user) => <UserResult key={user.id} user={user} />)}
      </div>
    );
    results.push(users);
  }

  if (candidateData.length) {
    const candidates = (
      <div key="candidates">
        {candidateData.map((candidate) => (
          <CandidateResult key={candidate.id} candidate={candidate} />
        ))}
      </div>
    );
    results.push(candidates);
  }

  let resultsOutput;

  if (results.length || loading) {
    resultsOutput = (
      <div style={{
        position: 'absolute', top: '100%', left: 0, width: '100%', background: '#fff', boxShadow: '0 0 5px rgba(0,0,0,0.5)', zIndex: 1000,
      }}
      >
        <LoadingAnimation loading={loading} inline />
        {results}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <form action="/users" method="GET">
        <input autoComplete="off" onChange={doSearch} className="form-control" type="search" name="q" ref={searchRef} placeholder="Search for people by name..." />
      </form>

      {resultsOutput}
    </div>
  );
}

function UserResult({ user }) {
  return (
    <a style={{ display: 'block', padding: '5px 10px', borderBottom: '1px solid #eee' }} href={`/users/${user.id}`}>
      <img
        alt="User profile"
        src={user.picture}
        style={{
          width: '22px', height: '22px', borderRadius: '100%', verticalAlign: 'middle', marginRight: '10px',
        }}
      />
      {user.name}
    </a>
  );
}

function CandidateResult({ candidate }) {
  return (
    <a style={{ display: 'block', padding: '5px 10px', borderBottom: '1px solid #eee' }} href={`/recruitment/${candidate.vacancy_id}/candidates/${candidate.uuid}`}>
      <span className="badge badge-info">Candidate</span>
      {' '}
      {candidate.name}
    </a>
  );
}

export default PeopleLayout;
